import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, CheckCircle, Clock, Wrench, Truck, Package, FileText, Camera, MessageSquare, XCircle, AlertTriangle, Loader2, Phone, Globe, MessageCircle, Eraser } from 'lucide-react'
import LanguageSwitcher from '../components/LanguageSwitcher'
import VacationNotice from '../components/VacationNotice'

const API_BASE = '/api'

const STATUS_STEP_CODES = [
  { status: 'requested', icon: Package },
  { status: 'approved', icon: CheckCircle },
  { status: 'pickup_scheduled', icon: Truck },
  { status: 'in_transit', icon: Truck },
  { status: 'received', icon: Package },
  { status: 'diagnosing', icon: Search },
  { status: 'not_repairable', icon: XCircle },
  { status: 'quote_sent', icon: FileText },
  { status: 'quote_approved', icon: CheckCircle },
  { status: 'repairing', icon: Wrench },
  { status: 'quality_check', icon: CheckCircle },
  { status: 'payment_pending', icon: Clock },
  { status: 'payment_confirmed', icon: CheckCircle },
  { status: 'return_shipped', icon: Truck },
  { status: 'delivered', icon: CheckCircle },
  { status: 'completed', icon: CheckCircle },
]

const STATUS_ORDER = STATUS_STEP_CODES.map(s => s.status)

export default function PublicRepairTrackingPage() {
  const { code: routeCode } = useParams()
  const { t } = useTranslation()
  const [code, setCode] = useState(routeCode || '')
  const [email, setEmail] = useState('')
  const [nif, setNif] = useState('')
  const [useNif, setUseNif] = useState(false)
  const [repair, setRepair] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Quote response
  const [quoteLoading, setQuoteLoading] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const [acceptChecked, setAcceptChecked] = useState(false)
  const [quoteNif, setQuoteNif] = useState('')
  const [signatureData, setSignatureData] = useState<string | null>(null)

  // Signature canvas
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const isDrawingRef = useRef(false)
  const lastPosRef = useRef({ x: 0, y: 0 })

  useEffect(() => {
    if (routeCode) setCode(routeCode)
  }, [routeCode])

  const getCanvasPos = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    if ('touches' in e) {
      const touch = e.touches[0]
      return { x: (touch.clientX - rect.left) * scaleX, y: (touch.clientY - rect.top) * scaleY }
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY }
  }, [])

  const startDrawing = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    isDrawingRef.current = true
    lastPosRef.current = getCanvasPos(e)
  }, [getCanvasPos])

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawingRef.current) return
    e.preventDefault()
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!ctx || !canvas) return
    const pos = getCanvasPos(e)
    ctx.beginPath()
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y)
    ctx.lineTo(pos.x, pos.y)
    ctx.strokeStyle = '#1e293b'
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke()
    lastPosRef.current = pos
  }, [getCanvasPos])

  const stopDrawing = useCallback(() => {
    if (isDrawingRef.current) {
      isDrawingRef.current = false
      const canvas = canvasRef.current
      if (canvas) setSignatureData(canvas.toDataURL('image/png'))
    }
  }, [])

  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (ctx && canvas) {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      setSignatureData(null)
    }
  }, [])

  const track = async () => {
    if (!code) return
    setLoading(true)
    setError('')
    try {
      const res = await fetch(`${API_BASE}/repairs/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, email: useNif ? undefined : email, nif: useNif ? nif : undefined }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Error')
      setRepair(data)
    } catch (err: any) {
      setError(err.message || t('repairs.tracking.notFound'))
      setRepair(null)
    } finally {
      setLoading(false)
    }
  }

  const respondQuote = async (response: 'approved' | 'rejected') => {
    if (response === 'rejected' && !rejectionReason) {
      alert(t('repairs.tracking.rejectionRequired'))
      return
    }
    if (response === 'approved' && !acceptChecked) {
      alert(t('repairs.tracking.acceptRequired'))
      return
    }
    if (!quoteNif.trim()) {
      alert(t('repairs.tracking.nifRequired', { defaultValue: 'Introduce tu DNI/NIF para continuar' }))
      return
    }
    if (!signatureData) {
      alert(t('repairs.tracking.signatureRequired', { defaultValue: 'La firma es obligatoria para continuar' }))
      return
    }
    setQuoteLoading(true)
    try {
      const res = await fetch(`${API_BASE}/repairs/${repair.code}/quote-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          response,
          rejection_reason: response === 'rejected' ? rejectionReason : undefined,
          nif: quoteNif.trim(),
          signature: signatureData,
        }),
      })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.detail || 'Error')
      }
      // Reload
      track()
    } catch (err: any) {
      alert(err.message)
    } finally {
      setQuoteLoading(false)
    }
  }

  const currentStepIndex = repair ? STATUS_ORDER.indexOf(repair.status) : -1

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
      <VacationNotice variant="repairs" />
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/jvs-logo.jpg" alt="JVS Informática" className="h-14" />
              <div className="hidden sm:block border-l border-slate-200 pl-3">
                <p className="text-sm font-semibold text-slate-800">{t('repairs.header.trackingTitle')}</p>
                <p className="text-xs text-slate-400">jvs-informatica.com</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <a href="/repairs" className="text-sm text-blue-600 hover:text-blue-800 font-medium">{t('repairs.header.requestLink')}</a>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 flex-1">
        {/* Search form */}
        {!repair && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">{t('repairs.tracking.searchTitle')}</h2>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">{t('repairs.tracking.codeLabel')}</label>
              <input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder={t('repairs.tracking.codePlaceholder')}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 font-mono" />
            </div>
            <div>
              <div className="flex items-center gap-4 mb-2">
                <button onClick={() => setUseNif(false)} className={`text-sm font-medium ${!useNif ? 'text-blue-600' : 'text-slate-400'}`}>{t('repairs.tracking.emailTab')}</button>
                <button onClick={() => setUseNif(true)} className={`text-sm font-medium ${useNif ? 'text-blue-600' : 'text-slate-400'}`}>{t('repairs.tracking.nifTab')}</button>
              </div>
              {useNif ? (
                <input value={nif} onChange={e => setNif(e.target.value)} placeholder={t('repairs.tracking.nifPlaceholder')}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500" />
              ) : (
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('repairs.tracking.emailPlaceholder')}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500" />
              )}
            </div>
            {error && <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{error}</p>}
            <button onClick={track} disabled={loading || !code || (!email && !nif)}
              className="w-full flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {t('repairs.tracking.search')}
            </button>
          </div>
        )}

        {/* Repair details */}
        {repair && (
          <div className="space-y-6">
            {/* Back button */}
            <button onClick={() => setRepair(null)} className="text-sm text-blue-600 hover:underline">
              {t('repairs.tracking.searchAnother')}
            </button>

            {/* Code + Status */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-slate-500">{t('repairs.tracking.repairCode')}</p>
                  <p className="text-2xl font-mono font-bold text-blue-600">{repair.code}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">{t('repairs.tracking.currentStatus')}</p>
                  <p className="text-lg font-semibold text-slate-900">{t(`repairs.repairStatuses.${repair.status}`, { defaultValue: repair.status })}</p>
                </div>
              </div>

              {/* Timeline */}
              <div className="relative">
                <div className="flex items-center justify-between">
                  {STATUS_STEP_CODES.filter(s => {
                    // Show relevant steps
                    if (['quote_rejected', 'waiting_parts', 'cancelled', 'rejected'].includes(repair.status)) return true
                    const idx = STATUS_ORDER.indexOf(s.status)
                    return idx <= Math.max(currentStepIndex, 0) + 2
                  }).slice(0, 8).map((step, i) => {
                    const Icon = step.icon
                    const stepIdx = STATUS_ORDER.indexOf(step.status)
                    const isDone = stepIdx <= currentStepIndex
                    const isCurrent = step.status === repair.status
                    return (
                      <div key={step.status} className="flex flex-col items-center flex-1">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isCurrent ? 'bg-blue-600 text-white ring-4 ring-blue-100' : isDone ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-400'}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <p className={`text-[10px] mt-1 text-center leading-tight ${isCurrent ? 'text-blue-600 font-semibold' : isDone ? 'text-emerald-600' : 'text-slate-400'}`}>{t(`repairs.repairStatuses.${step.status}`)}</p>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Device info */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">{t('repairs.tracking.deviceTitle')}</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-slate-500">{t('repairs.tracking.brand')}</span> <span className="font-medium">{repair.device_brand === 'Otro' ? repair.device_brand_other : repair.device_brand}</span></div>
                <div><span className="text-slate-500">{t('repairs.tracking.model')}</span> <span className="font-medium">{repair.device_model || '-'}</span></div>
                {repair.device_serial && <div><span className="text-slate-500">{t('repairs.tracking.serial')}</span> <span className="font-medium">{repair.device_serial}</span></div>}
                {repair.pickup_tracking && <div><span className="text-slate-500">{t('repairs.tracking.pickupTracking')}</span> <span className="font-mono font-medium">{repair.pickup_tracking}</span></div>}
                {repair.return_tracking && <div><span className="text-slate-500">{t('repairs.tracking.returnTracking')}</span> <span className="font-mono font-medium">{repair.return_tracking}</span></div>}
              </div>
            </div>

            {/* Quote section */}
            {repair.status === 'quote_sent' && repair.quote_total > 0 && (
              <div className="bg-white border-2 border-orange-200 rounded-xl p-5">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-orange-500" />{t('repairs.tracking.quoteTitle')}
                </h3>

                {/* Items (sale prices only) */}
                {repair.quote_items?.length > 0 && (
                  <div className="mb-4 space-y-1">
                    {repair.quote_items.map((qi: any, i: number) => (
                      <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
                        <span className="text-slate-700">
                          {qi.description}
                          {qi.quantity > 1 && ` × ${qi.quantity}`}
                        </span>
                        <span className="font-medium">{qi.total_sale?.toFixed(2)} €</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="bg-slate-50 rounded-lg p-4 mb-4">
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">{t('repairs.tracking.subtotal')}</span><span>{repair.quote_total_net?.toFixed(2)} €</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">{t('repairs.tracking.vat')}</span><span>+{repair.quote_vat_amount?.toFixed(2)} €</span></div>
                    {repair.diagnostic_fee > 0 && (
                      <div className="flex justify-between text-emerald-600"><span>{t('repairs.tracking.diagnosticDiscount')}</span><span>-{repair.diagnostic_fee?.toFixed(2)} €</span></div>
                    )}
                    <div className="border-t border-slate-300 pt-1.5 flex justify-between text-lg font-bold">
                      <span>{t('repairs.tracking.total')}</span>
                      <span className="text-blue-600">{repair.quote_total?.toFixed(2)} €</span>
                    </div>
                  </div>
                </div>

                {repair.quote_valid_until && (
                  <p className="text-xs text-slate-500 mb-4">{t('repairs.tracking.validUntil', { date: new Date(repair.quote_valid_until).toLocaleDateString() })}</p>
                )}

                {repair.quote_notes && (
                  <p className="text-sm text-slate-600 bg-blue-50 p-3 rounded-lg mb-4">{repair.quote_notes}</p>
                )}

                {/* Accept/Reject */}
                <div className="border-t border-slate-200 pt-4 space-y-4">
                  <label className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg cursor-pointer">
                    <input type="checkbox" checked={acceptChecked} onChange={e => setAcceptChecked(e.target.checked)}
                      className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 mt-0.5" />
                    <span className="text-sm text-emerald-800">{t('repairs.tracking.acceptQuote')}</span>
                  </label>

                  {/* NIF */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t('repairs.tracking.nifLabel', { defaultValue: 'DNI / NIF' })} <span className="text-red-500">*</span>
                    </label>
                    <input value={quoteNif} onChange={e => setQuoteNif(e.target.value.toUpperCase())}
                      placeholder={t('repairs.tracking.nifQuotePlaceholder', { defaultValue: '12345678A' })}
                      maxLength={15}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono" />
                  </div>

                  {/* Signature */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-slate-700">
                        {t('repairs.tracking.signatureLabel', { defaultValue: 'Firma' })} <span className="text-red-500">*</span>
                      </label>
                      {signatureData && (
                        <button onClick={clearSignature} className="flex items-center gap-1 text-xs text-slate-500 hover:text-red-500">
                          <Eraser className="w-3.5 h-3.5" />
                          {t('repairs.tracking.clearSignature', { defaultValue: 'Borrar firma' })}
                        </button>
                      )}
                    </div>
                    <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white overflow-hidden touch-none">
                      <canvas
                        ref={canvasRef}
                        width={560}
                        height={160}
                        className="w-full h-32 cursor-crosshair"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      {t('repairs.tracking.signatureHint', { defaultValue: 'Dibuja tu firma con el ratón o el dedo' })}
                    </p>
                  </div>

                  {/* Rejection reason */}
                  <div>
                    <input value={rejectionReason} onChange={e => setRejectionReason(e.target.value)}
                      placeholder={t('repairs.tracking.rejectionPlaceholder')}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" />
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3">
                    <button onClick={() => respondQuote('approved')} disabled={quoteLoading || !acceptChecked || !quoteNif.trim() || !signatureData}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                      {quoteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      {t('repairs.tracking.acceptButton')}
                    </button>
                    <button onClick={() => respondQuote('rejected')} disabled={quoteLoading || !quoteNif.trim() || !signatureData}
                      className="px-4 py-2.5 text-red-600 font-medium border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors">
                      {t('repairs.tracking.rejectButton')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Quote response info */}
            {repair.quote_response && (
              <div className={`bg-white border-2 rounded-xl p-4 ${repair.quote_response === 'approved' ? 'border-emerald-200' : 'border-red-200'}`}>
                <div className="flex items-center gap-2">
                  {repair.quote_response === 'approved' ? (
                    <><CheckCircle className="w-5 h-5 text-emerald-500" /><span className="font-medium text-emerald-700">{t('repairs.tracking.quoteAccepted')}</span></>
                  ) : (
                    <><XCircle className="w-5 h-5 text-red-500" /><span className="font-medium text-red-700">{t('repairs.tracking.quoteRejected')}</span></>
                  )}
                  {repair.quote_response_at && <span className="text-xs text-slate-400 ml-auto">{new Date(repair.quote_response_at).toLocaleString()}</span>}
                </div>
                {repair.quote_total > 0 && repair.quote_response === 'approved' && (
                  <p className="text-sm text-slate-600 mt-1">{t('repairs.tracking.total')}: <strong>{repair.quote_total?.toFixed(2)} €</strong></p>
                )}
                {repair.quote_response_nif && (
                  <p className="text-xs text-slate-500 mt-2">DNI/NIF: <span className="font-mono">{repair.quote_response_nif}</span></p>
                )}
                {repair.quote_response_signature && (
                  <div className="mt-2">
                    <p className="text-xs text-slate-500 mb-1">{t('repairs.tracking.signatureLabel', { defaultValue: 'Firma' })}:</p>
                    <img src={repair.quote_response_signature} alt="Firma" className="h-16 border border-slate-200 rounded bg-white" />
                  </div>
                )}
              </div>
            )}

            {/* History */}
            {repair.history?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">{t('repairs.tracking.history')}</h3>
                <div className="space-y-3">
                  {[...repair.history].reverse().map((h: any, i: number) => (
                    <div key={i} className="flex gap-3">
                      <div className="w-2 h-2 mt-2 rounded-full bg-blue-400 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-slate-700">{h.description}</p>
                        <p className="text-xs text-slate-400">{h.created_at ? new Date(h.created_at).toLocaleString() : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Public comments */}
            {repair.comments?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">{t('repairs.tracking.teamComments')}</h3>
                <div className="space-y-3">
                  {repair.comments.map((c: any, i: number) => (
                    <div key={i} className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-slate-700">{c.comment}</p>
                      {c.attachments?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {c.attachments.map((att: any) => (
                            <a key={att.id} href={`/api/repairs/comment-attachment/${att.id}?public=1`} target="_blank" rel="noreferrer"
                              className="block rounded-lg overflow-hidden border border-blue-200 hover:border-blue-400 transition-colors">
                              <img src={`/api/repairs/comment-attachment/${att.id}?public=1`} alt=""
                                className="h-24 w-auto max-w-[160px] object-cover" />
                            </a>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-slate-400 mt-1">{c.user_name} — {c.created_at ? new Date(c.created_at).toLocaleString() : ''}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-auto">
        <div className="max-w-2xl mx-auto px-4 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <img src="/jvs-logo.jpg" alt="JVS" className="h-9 opacity-70" />
              <span>Especialistas en portátiles</span>
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:+34942719735" className="flex items-center gap-1 hover:text-slate-700"><Phone className="w-3.5 h-3.5" />942 71 97 35</a>
              <a href="https://wa.me/34644464960" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-green-600"><MessageCircle className="w-3.5 h-3.5" />644 46 49 60</a>
              <a href="https://jvs-informatica.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-slate-700"><Globe className="w-3.5 h-3.5" />jvs-informatica.com</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
